import React, { useState } from 'react';
import {
    Box,
    Card,
    CardContent,
    Typography,
    TextField,
    Button,
    Alert,
    CircularProgress,
    Grid,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Chip
} from '@mui/material';
import {
    ArrowBack,
    Send as SendIcon,
    Help as HelpIcon,
    ExpandMore as ExpandMoreIcon,
    Email as EmailIcon,
    Phone as PhoneIcon,
    Schedule as ScheduleIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';

const HelpScreen = () => {
    const navigate = useNavigate();
    const { currentUser } = useAuth();

    // Form state
    const [formData, setFormData] = useState({
        name: currentUser?.displayName || '',
        email: currentUser?.email || '',
        subject: '',
        message: ''
    });
    const [loading, setLoading] = useState(false);
    const [success, setSuccess] = useState(false);
    const [error, setError] = useState('');

    // FAQ data
    const faqData = [
        {
            question: "How do I place a bet using your odds comparison?",
            answer: "Our platform shows you the best odds across multiple sportsbooks. Simply view the odds, click 'Visit Site' on your preferred bookmaker, and place your bet directly on their platform. We help you find the best value, but all betting is done through the individual sportsbooks."
        },
        {
            question: "How does BettorAI generate picks?",
            answer: "BettorAI uses advanced machine learning algorithms to analyze thousands of data points including line movements, team statistics, injury reports, weather conditions, and historical performance. The AI provides picks with confidence levels and detailed analysis to help inform your betting decisions."
        },
        {
            question: "What is the affiliate program?",
            answer: "Our affiliate program allows you to earn commission by referring new users. You get paid for every user who signs up with your referral code. Check the Affiliate Program section in your account for your unique referral link and QR code."
        },
        {
            question: "How accurate are the odds displayed?",
            answer: "Our odds are updated in real-time from multiple sportsbooks. However, odds can change rapidly, so we recommend verifying the current odds on the sportsbook's website before placing your bet. We strive to provide the most current information available."
        },
        {
            question: "What sports and leagues do you cover?",
            answer: "We cover major sports including NFL, NBA, MLB, NHL, NCAA Football, NCAA Basketball, and more. Our coverage includes both professional and college sports, with odds for various bet types including moneylines, spreads, and totals."
        }
    ];

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError('');
        setSuccess(false);

        try {
            // Create mailto link with form data
            const subject = encodeURIComponent(`Support Request: ${formData.subject}`);
            const body = encodeURIComponent(
                `Name: ${formData.name}\n` +
                `Email: ${formData.email}\n` +
                `User ID: ${currentUser?.uid || 'Not logged in'}\n\n` +
                `Message:\n${formData.message}`
            );

            const mailtoLink = `mailto:help@bettoradvantage.com?subject=${subject}&body=${body}`;

            // Open email client
            window.location.href = mailtoLink;

            // Show success message
            setSuccess(true);

            // Reset form
            setFormData({
                name: currentUser?.displayName || '',
                email: currentUser?.email || '',
                subject: '',
                message: ''
            });

        } catch (err) {
            console.error('Error sending email:', err);
            setError('Failed to open email client. Please try emailing help@bettoradvantage.com directly.');
        } finally {
            setLoading(false);
        }
    };

    const isFormValid = formData.name && formData.email && formData.subject && formData.message;

    return (
        <Box sx={{ py: 2 }}>
            {/* Back Button */}
            <Box sx={{ mb: 2 }}>
                <Button
                    startIcon={<ArrowBack />}
                    onClick={() => navigate('/account')}
                >
                    Back
                </Button>
            </Box>

            {/* Header */}
            <Box sx={{ mb: 3, textAlign: 'center' }}>
                <HelpIcon sx={{ fontSize: 48, color: 'primary.main', mb: 1 }} />
                <Typography variant="h4" gutterBottom sx={{ fontWeight: 600 }}>
                    Help & Support
                </Typography>
                <Typography variant="body1" color="text.secondary">
                    Get the help you need or contact our support team
                </Typography>
            </Box>

            <Grid container spacing={3}>
                {/* Contact Form */}
                <Grid item xs={12} md={6}>
                    <Card>
                        <CardContent>
                            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <EmailIcon color="primary" />
                                Contact Support
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                                Send us a message and we'll get back to you as soon as possible.
                            </Typography>

                            {success && (
                                <Alert severity="success" sx={{ mb: 2 }}>
                                    Email client opened successfully! If it didn't open automatically, please email help@bettoradvantage.com directly.
                                </Alert>
                            )}

                            {error && (
                                <Alert severity="error" sx={{ mb: 2 }}>
                                    {error}
                                </Alert>
                            )}

                            <Box component="form" onSubmit={handleSubmit}>
                                <Grid container spacing={2}>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            fullWidth
                                            label="Name"
                                            name="name"
                                            value={formData.name}
                                            onChange={handleInputChange}
                                            required
                                            disabled={loading}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            fullWidth
                                            label="Email"
                                            name="email"
                                            type="email"
                                            value={formData.email}
                                            onChange={handleInputChange}
                                            required
                                            disabled={loading}
                                        />
                                    </Grid>
                                    <Grid item xs={12}>
                                        <TextField
                                            fullWidth
                                            label="Subject"
                                            name="subject"
                                            value={formData.subject}
                                            onChange={handleInputChange}
                                            required
                                            disabled={loading}
                                            placeholder="Brief description of your issue"
                                        />
                                    </Grid>
                                    <Grid item xs={12}>
                                        <TextField
                                            fullWidth
                                            label="Message"
                                            name="message"
                                            multiline
                                            rows={4}
                                            value={formData.message}
                                            onChange={handleInputChange}
                                            required
                                            disabled={loading}
                                            placeholder="Please provide as much detail as possible about your issue or question"
                                        />
                                    </Grid>
                                    <Grid item xs={12}>
                                        <Button
                                            type="submit"
                                            variant="contained"
                                            fullWidth
                                            disabled={loading || !isFormValid}
                                            startIcon={loading ? <CircularProgress size={20} /> : <SendIcon />}
                                            sx={{ py: 1.5 }}
                                        >
                                            {loading ? 'Opening Email...' : 'Send Message'}
                                        </Button>
                                    </Grid>
                                </Grid>
                            </Box>

                            {/* Direct Contact Info */}
                            <Box sx={{
                                mt: 3,
                                p: 2,
                                backgroundColor: 'primary.light',
                                borderRadius: 1,
                                border: 1,
                                borderColor: 'primary.main'
                            }}>
                                <Typography variant="subtitle2" gutterBottom sx={{ color: 'primary.contrastText', fontWeight: 'bold' }}>
                                    Direct Contact:
                                </Typography>
                                <Typography variant="body2" sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1,
                                    mb: 1,
                                    color: 'primary.contrastText'
                                }}>
                                    <EmailIcon sx={{ fontSize: 16, color: 'primary.contrastText' }} />
                                    help@bettoradvantage.com
                                </Typography>
                                <Typography variant="body2" sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1,
                                    color: 'primary.contrastText'
                                }}>
                                    <ScheduleIcon sx={{ fontSize: 16, color: 'primary.contrastText' }} />
                                    Response time: 24-48 hours
                                </Typography>
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>

                {/* FAQ Section */}
                <Grid item xs={12} md={6}>
                    <Card>
                        <CardContent>
                            <Typography variant="h6" gutterBottom>
                                Frequently Asked Questions
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                                Find quick answers to common questions
                            </Typography>

                            {faqData.map((faq, index) => (
                                <Accordion key={index} sx={{ mb: 1 }}>
                                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                        <Typography variant="subtitle2">
                                            {faq.question}
                                        </Typography>
                                    </AccordionSummary>
                                    <AccordionDetails>
                                        <Typography variant="body2" color="text.secondary">
                                            {faq.answer}
                                        </Typography>
                                    </AccordionDetails>
                                </Accordion>
                            ))}

                            {/* Quick Help Topics */}
                            <Box sx={{ mt: 3 }}>
                                <Typography variant="subtitle2" gutterBottom>
                                    Quick Help Topics:
                                </Typography>
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                    {[
                                        'Account Issues',
                                        'Betting Help',
                                        'Technical Problems',
                                        'Affiliate Program',
                                        'BettorAI',
                                        'Odds Questions'
                                    ].map((topic) => (
                                        <Chip
                                            key={topic}
                                            label={topic}
                                            size="small"
                                            variant="outlined"
                                            onClick={() => setFormData(prev => ({ ...prev, subject: topic }))}
                                            sx={{ cursor: 'pointer' }}
                                        />
                                    ))}
                                </Box>
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>
        </Box>
    );
};

export default HelpScreen;